import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import SalesOrderItems from './SalesOrderItems';
import '../inventory.css';

const SalesOrders = ({ organization }) => {
  const { t } = useTranslation();
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tableColumns, setTableColumns] = useState([]);
  const [columnSettings, setColumnSettings] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showColumnMenu, setShowColumnMenu] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showItemsModal, setShowItemsModal] = useState(null);

  // Add state for dropdown options
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    if (organization) {
      const loadData = async () => {
        loadColumnSettings();
        await loadSalesOrders();
        await loadDropdownData();
      };
      loadData();
    }
  }, [organization, filterStatus, filterCustomerId]);

  // Load column settings from localStorage
  const loadColumnSettings = () => {
    const saved = localStorage.getItem(`salesOrderColumns_${organization}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setColumnSettings(parsed.settings || {});
        setSortConfig(parsed.sort || { key: null, direction: 'asc' });
        return parsed.settings || {};
      } catch (e) {
        console.error('Error loading column settings:', e);
        return {};
      }
    }
    return {};
  };

  // Save column settings to localStorage
  const saveColumnSettings = (settings, sort) => {
    localStorage.setItem(`salesOrderColumns_${organization}`, JSON.stringify({
      settings,
      sort
    }));
  };

  // Load dropdown data for selects
  const loadDropdownData = async () => {
    try {
      const customersResponse = await inventoryApi.getCustomers(organization);
      setCustomers(Array.isArray(customersResponse) ? customersResponse : (customersResponse.data || []));
    } catch (err) {
      console.error('Error loading dropdown data:', err);
    }
  };

  const loadSalesOrders = async () => {
    if (!organization) {
      setError('Organization is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await inventoryApi.getSalesOrders(
        organization,
        filterCustomerId || null,
        filterStatus || null
      );
      const ordersData = Array.isArray(response) ? response : (response.data || []);
      setSalesOrders(ordersData);

      // Generate dynamic columns based on the first sales order
      if (ordersData.length > 0) {
        const saved = localStorage.getItem(`salesOrderColumns_${organization}`);
        let savedSettings = {};
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            savedSettings = parsed.settings || {};
          } catch (e) {
            console.error('Error parsing saved settings:', e);
          }
        }

        const firstOrder = ordersData[0];
        const columns = Object.keys(firstOrder)
          .filter(key => key !== 'id' && key !== 'organization')
          .map((key, index) => ({
            key,
            label: formatColumnLabel(key),
            visible: savedSettings[key]?.visible !== false,
            order: savedSettings[key]?.order ?? index
          }));
        columns.sort((a, b) => a.order - b.order);
        setTableColumns(columns);
      }
    } catch (err) {
      console.error('Error loading sales orders:', err);
      setError(err.message || 'Failed to load sales orders');
    } finally {
      setLoading(false);
    }
  };

  // Toggle column visibility
  const toggleColumnVisibility = (key) => {
    const newSettings = {
      ...columnSettings,
      [key]: {
        ...columnSettings[key],
        visible: !(columnSettings[key]?.visible ?? true)
      }
    };
    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);

    setTableColumns(prev => prev.map(col =>
      col.key === key ? { ...col, visible: !col.visible } : col
    ));
  };

  // Move column up in order
  const moveColumnUp = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex <= 0) return;

    const newColumns = [...tableColumns];
    [newColumns[currentIndex - 1], newColumns[currentIndex]] =
      [newColumns[currentIndex], newColumns[currentIndex - 1]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Move column down in order
  const moveColumnDown = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex >= tableColumns.length - 1) return;

    const newColumns = [...tableColumns];
    [newColumns[currentIndex], newColumns[currentIndex + 1]] =
      [newColumns[currentIndex + 1], newColumns[currentIndex]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    const newSortConfig = { key, direction };
    setSortConfig(newSortConfig);
    saveColumnSettings(columnSettings, newSortConfig);
  };

  // Sort sales orders
  const sortedOrders = React.useMemo(() => {
    let sortableOrders = [...salesOrders];
    if (sortConfig.key) {
      sortableOrders.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableOrders;
  }, [salesOrders, sortConfig]);

  const filteredOrders = sortedOrders.filter(order => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return Object.values(order).some(value =>
      value?.toString().toLowerCase().includes(term)
    );
  });

  // Get visible columns
  const visibleColumns = tableColumns.filter(col => col.visible);

  // Reset column settings
  const resetColumnSettings = () => {
    localStorage.removeItem(`salesOrderColumns_${organization}`);
    setColumnSettings({});
    setSortConfig({ key: null, direction: 'asc' });
    loadSalesOrders();
  };

  // Helper function to format column labels
  const formatColumnLabel = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  // Helper function to format cell values
  const formatCellValue = (value, key) => {
    if (value === null || value === undefined) return '-';

    // Format boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    // Format price/cost/total fields
    if ((key.includes('total') || key.includes('amount') || key.includes('price')) && typeof value === 'number') {
      return `$${value.toFixed(2)}`;
    }

    // Format dates
    if (key.includes('date') || key.includes('Date') || key.includes('created') || key.includes('updated')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      } catch (e) {
        return value;
      }
    }

    return value.toString();
  };

  // Helper function to determine if field should be a select
  const isSelectField = (key) => {
    return key.endsWith('Id') ||
      key === 'customerId' ||
      key === 'status' ||
      key === 'paymentTerms' ||
      key === 'shippingMethod';
  };

  // Helper function to get select options
  const getSelectOptions = (key) => {
    if (key === 'customerId') {
      return customers.map(cust => ({ value: cust.id, label: cust.name || cust.customerName }));
    }
    if (key === 'status') {
      return [
        { value: 'draft', label: 'Draft' },
        { value: 'confirmed', label: 'Confirmed' },
        { value: 'processing', label: 'Processing' },
        { value: 'shipped', label: 'Shipped' },
        { value: 'delivered', label: 'Delivered' },
        { value: 'cancelled', label: 'Cancelled' }
      ];
    }
    if (key === 'paymentTerms') {
      return [
        { value: 'net30', label: 'Net 30' },
        { value: 'net60', label: 'Net 60' },
        { value: 'cod', label: 'Cash on Delivery' },
        { value: 'prepaid', label: 'Prepaid' }
      ];
    }
    if (key === 'shippingMethod') {
      return [
        { value: 'standard', label: 'Standard Shipping' },
        { value: 'express', label: 'Express Shipping' },
        { value: 'overnight', label: 'Overnight' },
        { value: 'pickup', label: 'Customer Pickup' }
      ];
    }
    return [];
  };

  const handleOpenModal = (order = null) => {
    if (order) {
      setEditingId(order.id);
      setEditFormData({ ...order });
    } else {
      setEditingId(null);
      const defaults = {
        status: 'draft',
        paymentTerms: 'net30',
        shippingMethod: 'standard'
      };
      tableColumns.forEach(col => {
        if (!(col.key in defaults)) {
          defaults[col.key] = '';
        }
      });
      setEditFormData(defaults);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this sales order?')) return;

    try {
      await inventoryApi.deleteSalesOrder(orderId);
      await loadSalesOrders();
    } catch (err) {
      alert('Error deleting sales order: ' + err.message);
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await inventoryApi.updateSalesOrderStatus(orderId, newStatus);
      await loadSalesOrders();
    } catch (err) {
      alert('Error updating status: ' + err.message);
    }
  };

  const handleSave = async () => {
    try {
      const orderData = {
        ...editFormData,
        organization,
        // Ensure numeric fields are parsed
        totalAmount: parseFloat(editFormData.totalAmount) || 0
      };

      if (editingId) {
        await inventoryApi.updateSalesOrder(editingId, orderData);
      } else {
        await inventoryApi.createSalesOrder(orderData);
      }

      await loadSalesOrders();
      handleCloseModal();
    } catch (err) {
      alert('Error saving sales order: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="pm-page-card pm-skeleton" aria-busy="true">
        {t('inventory.loading_sales_orders_message')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="pm-page-card">
        <p style={{ color: 'var(--pico-del-color)' }}>
          {t('inventory.error_prefix')} {error}
        </p>
        <button className="pm-btn-primary" onClick={loadSalesOrders}>
          {t('inventory.retry_button')}
        </button>
      </div>
    );
  }

  return (
    <div className="pm-page-card">
      <div className="pm-header">
        <h2 className="pm-header-title">{t('inventory.sales_orders_title')}</h2>
        <div className="pm-header-actions">
          <button className="pm-btn-primary" onClick={() => handleOpenModal()}>
            <span aria-hidden="true">➕</span>
            <span>{t('inventory.add_sales_order_button')}</span>
          </button>
        </div>
      </div>

      <div className="pm-toolbar">
        <div className="pm-search-wrap">
          <input
            type="search"
            placeholder={t('inventory.search_sales_orders')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="pm-filter-wrap">
          <select
            value={filterCustomerId}
            onChange={(e) => setFilterCustomerId(e.target.value)}
          >
            <option value="">{t('inventory.all_customers_filter')}</option>
            {customers.map(customer => (
              <option key={customer.id} value={customer.id}>
                {customer.name || customer.customerName}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">{t('inventory.all_statuses_filter')}</option>
            <option value="draft">{t('inventory.draft_status')}</option>
            <option value="confirmed">{t('inventory.confirmed_status')}</option>
            <option value="processing">{t('inventory.processing_status')}</option>
            <option value="shipped">{t('inventory.shipped_status')}</option>
            <option value="delivered">{t('inventory.delivered_status')}</option>
            <option value="cancelled">{t('inventory.cancelled_status')}</option>
          </select>
        </div>
      </div>

      {salesOrders.length === 0 ? (
        <div className="pm-empty-state">
          <div className="pm-empty-state-icon" aria-hidden="true">🛒</div>
          <h3>{t('inventory.no_sales_orders_message')}</h3>
          <button className="pm-btn-primary" onClick={() => handleOpenModal()}>
            <span aria-hidden="true">➕</span>
            {t('inventory.add_sales_order_button')}
          </button>
        </div>
      ) : (
        <div className="pm-table-wrap">
          <table className="pm-table" role="grid">
            <thead>
              <tr>
                <th style={{ width: '100px' }}>
                  <div className="pm-header-content">
                    {tableColumns.filter(c => !c.visible).length > 0 && (
                      <button
                        className="pm-column-btn visibility-btn"
                        onClick={() => setShowColumnMenu(showColumnMenu === 'hidden' ? null : 'hidden')}
                        title={t('inventory.show_hidden_columns_title')}
                      >
                        ⊕
                      </button>
                    )}
                    <div className="pm-header-label">{t('inventory.actions_header')}</div>
                  </div>
                  {showColumnMenu === 'hidden' && (
                    <div className="pm-column-menu">
                      <div className="menu-header">{t('inventory.hidden_columns_menu_header')}</div>
                      {tableColumns.filter(c => !c.visible).map(col => (
                        <button key={col.key} onClick={() => toggleColumnVisibility(col.key)}>
                          {t('inventory.show_column_button')} {col.label}
                        </button>
                      ))}
                      <hr />
                      <button onClick={() => {
                        resetColumnSettings();
                        setShowColumnMenu(null);
                      }}>
                        {t('inventory.reset_all_button')}
                      </button>
                    </div>
                  )}
                </th>
                {visibleColumns.map((col, index) => (
                  <th key={col.key} className="pm-sortable-header">
                    <div className="pm-header-content">
                      <div className="pm-header-controls">
                        <button
                          className="pm-column-btn"
                          onClick={() => moveColumnUp(col.key)}
                          disabled={index === 0}
                          title={t('inventory.move_left_title')}
                        >
                          ◀
                        </button>
                        <button
                          className="pm-column-btn"
                          onClick={() => moveColumnDown(col.key)}
                          disabled={index === visibleColumns.length - 1}
                          title={t('inventory.move_right_title')}
                        >
                          ▶
                        </button>
                        <button
                          className="pm-column-btn visibility-btn"
                          onClick={() => setShowColumnMenu(showColumnMenu === col.key ? null : col.key)}
                          title={t('inventory.column_options_title')}
                        >
                          ⋮
                        </button>
                      </div>
                      <div className="pm-header-label" onClick={() => handleSort(col.key)}>
                        {col.label}
                        {sortConfig.key === col.key && (
                          <span className="pm-sort-indicator">
                            {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                          </span>
                        )}
                      </div>
                    </div>
                    {showColumnMenu === col.key && (
                      <div className="pm-column-menu">
                        <button onClick={() => {
                          toggleColumnVisibility(col.key);
                          setShowColumnMenu(null);
                        }}>
                          {t('inventory.hide_column_button')}
                        </button>
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td>
                    <div className="pm-row-actions">
                      <button
                        className="pm-row-btn"
                        onClick={() => setShowItemsModal(order.id)}
                        title={t('inventory.view_items_tooltip')}
                      >
                        📦
                      </button>
                      <button
                        className="pm-row-btn edit"
                        onClick={() => handleOpenModal(order)}
                        title={t('inventory.edit_tooltip')}
                      >
                        ✏️
                      </button>
                      <button
                        className="pm-row-btn delete"
                        onClick={() => handleDelete(order.id)}
                        title={t('inventory.delete_tooltip')}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                  {visibleColumns.map(col => (
                    <td key={col.key}>
                      {col.key === 'status' ? (
                        <span className={`pm-status-badge ${order[col.key]?.toString().toLowerCase()}`}>
                          {formatCellValue(order[col.key], col.key)}
                        </span>
                      ) : (
                        formatCellValue(order[col.key], col.key)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <div className="pm-modal-overlay">
          <div className="pm-modal-content">
            <div className="pm-modal-header">
              <h2>{editingId ? t('inventory.edit_sales_order_title') : t('inventory.add_sales_order_title')}</h2>
              <button className="pm-modal-close" onClick={handleCloseModal}>&times;</button>
            </div>
            <div className="pm-modal-body">
              <div className="pm-form-grid">
                {tableColumns.map(col => {
                  const value = editFormData[col.key] ?? '';
                  const isBoolean = typeof value === 'boolean';
                  const isNumber = col.key.includes('total') || col.key.includes('amount') || col.key.includes('Total') || col.key.includes('Amount');
                  const isTextarea = col.key === 'notes' || col.key === 'description';
                  const isSelect = isSelectField(col.key);
                  const selectOptions = isSelect ? getSelectOptions(col.key) : [];

                  if (isBoolean) {
                    return (
                      <div key={col.key} className="pm-form-group full-width">
                        <label>
                          <input
                            type="checkbox"
                            role="switch"
                            checked={!!value}
                            onChange={(e) => setEditFormData({ ...editFormData, [col.key]: e.target.checked })}
                          />
                          {col.label}
                        </label>
                      </div>
                    );
                  }

                  return (
                    <div key={col.key} className="pm-form-group">
                      <label>{col.label}</label>
                      {isSelect ? (
                        <select
                          value={value}
                          onChange={(e) => setEditFormData({ ...editFormData, [col.key]: e.target.value })}
                        >
                          <option value="">{t('inventory.select_placeholder')} {col.label} --</option>
                          {selectOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : isTextarea ? (
                        <textarea
                          value={value}
                          onChange={(e) => setEditFormData({ ...editFormData, [col.key]: e.target.value })}
                        />
                      ) : (
                        <input
                          type={isNumber ? 'number' : col.key.includes('date') || col.key.includes('Date') ? 'date' : 'text'}
                          step={isNumber ? '0.01' : undefined}
                          value={value}
                          onChange={(e) => setEditFormData({ ...editFormData, [col.key]: e.target.value })}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="pm-modal-footer">
              <button className="pm-btn-secondary" onClick={handleCloseModal}>
                {t('inventory.cancel_button')}
              </button>
              <button className="pm-btn-primary" onClick={handleSave}>
                <span aria-hidden="true">💾</span>
                {editingId ? t('inventory.update_button') : t('inventory.create_button')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showItemsModal && (
        <SalesOrderItems
          salesOrderId={showItemsModal}
          organization={organization}
          onClose={() => setShowItemsModal(null)}
        />
      )}
    </div>
  );
};

export default SalesOrders;
